import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Box,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import { ChatUser } from '../../types/chat';
import { chatAPI } from '../../services/api';
import ChatAvatar from './ChatAvatar';

interface NewConversationDialogProps {
  open: boolean;
  onClose: () => void;
  onSelectUser: (userId: number) => void;
  currentUserId: number;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const NewConversationDialog: React.FC<NewConversationDialogProps> = ({
  open,
  onClose,
  onSelectUser,
  currentUserId,
}) => {
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<ChatUser[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadUsers();
    }
  }, [open]);

  useEffect(() => {
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      setFilteredUsers(
        users.filter((user) =>
          user.full_name.toLowerCase().includes(searchLower)
        )
      );
    } else {
      setFilteredUsers(users);
    }
  }, [search, users]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await chatAPI.getChatUsers();
      // Filter out current user
      const otherUsers = response.filter((u) => u.id !== currentUserId);
      setUsers(otherUsers);
      setFilteredUsers(otherUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId: number) => {
    onSelectUser(userId);
    onClose();
    setSearch('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: OUTLOOK_BLUE }}>
          New Conversation
        </Typography>
        <Box
          onClick={onClose}
          sx={{
            cursor: 'pointer',
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <CloseIcon />
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Search input */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&.Mui-focused fieldset': {
                borderColor: OUTLOOK_BLUE,
              },
            },
          }}
        />

        {/* User list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: OUTLOOK_BLUE }} />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}
          >
            {search ? 'No users found' : 'No users available'}
          </Typography>
        ) : (
          <List sx={{ maxHeight: 300, overflowY: 'auto' }}>
            {filteredUsers.map((user) => (
              <ListItem key={user.id} disablePadding>
                <ListItemButton
                  onClick={() => handleSelectUser(user.id)}
                  sx={{
                    borderRadius: 1,
                    '&:hover': { backgroundColor: 'background.default' },
                  }}
                >
                  <ListItemAvatar>
                    <ChatAvatar
                      name={user.full_name}
                      isOnline={user.is_online}
                      lastSeenAt={user.last_seen_at}
                      size={40}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={user.full_name}
                    secondary={user.is_online ? 'Online' : 'Offline'}
                    primaryTypographyProps={{ fontWeight: 500 }}
                    secondaryTypographyProps={{
                      sx: { color: user.is_online ? '#4caf50' : 'text.secondary' },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewConversationDialog;
